import { useAuth, useClerk } from '@clerk/react'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Check, Copy, ExternalLink, KeyRound, LoaderCircle, PackageOpen, Settings2, ShieldCheck, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Header } from '../components/Header'
import { ProductImage } from '../components/ProductImage'
import { apiRequest, formatDate, formatMoney, orderStatusLabels, planLabels } from '../lib/api'
import type { AccountProfile, Order } from '../types'

function AccessKey({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="access-key">
      <span className="access-key__label"><KeyRound size={12} /> Access key</span>
      <code>{value}</code>
      <button type="button" onClick={() => void copy()} aria-label="Copy access key">
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  )
}

export function AccountPage() {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth()
  const { openSignIn } = useClerk()

  const profile = useQuery({
    queryKey: ['account', userId],
    queryFn: async () => {
      const token = await getToken()
      return apiRequest<{ data: AccountProfile }>('/api/me', { token })
    },
    enabled: Boolean(isLoaded && isSignedIn),
    retry: false,
  })

  const orders = useQuery({
    queryKey: ['orders', userId],
    queryFn: async () => {
      const token = await getToken()
      return apiRequest<{ data: Order[] }>('/api/me/orders', { token })
    },
    enabled: Boolean(isLoaded && isSignedIn),
    retry: false,
  })

  return (
    <div className="page-shell">
      <Header />
      <main className="dashboard-page section-frame">
        {!isLoaded ? (
          <div className="dashboard-loading"><LoaderCircle className="spin" /><span>Verifying session…</span></div>
        ) : !isSignedIn ? (
          <section className="auth-gate">
            <div className="auth-gate__icon"><ShieldCheck /></div>
            <span className="eyebrow">PRIVATE AREA</span>
            <h1>Your control center.</h1>
            <p>Sign in to check purchases, manage your subscription and publish verified reviews.</p>
            <button className="button button--primary" type="button" onClick={() => void openSignIn({})}>Sign in <ArrowRight size={17} /></button>
          </section>
        ) : (
          <>
            <section className="dashboard-hero">
              <div>
                <span className="eyebrow">ACCOUNT // CHEATSECLIPSE</span>
                <h1>Hey, {profile.data?.data.displayName ?? 'Eclipse user'}.</h1>
                <p>All your access, in one place.</p>
              </div>
              <div className="dashboard-hero__actions">
                {profile.data?.data.isAdmin ? <Link className="button button--secondary" to="/admin"><Settings2 size={16} /> Admin</Link> : null}
              </div>
            </section>

            {profile.isError || orders.isError ? (
              <div className="form-message form-message--error dashboard-message">The account could not be loaded. Check that the API and Clerk are configured.</div>
            ) : null}

            <section className="account-grid">
              <article className="account-card account-card--identity">
                <span className="account-card__label">IDENTITY</span>
                <div className="account-profile">
                  <span>{profile.data?.data.avatarUrl ? <img src={profile.data.data.avatarUrl} alt="" /> : profile.data?.data.displayName.slice(0, 1)}</span>
                  <div><strong>{profile.data?.data.displayName}</strong><small>{profile.data?.data.email}</small></div>
                </div>
                <div className="verified-line"><ShieldCheck size={14} /> Session protected by Clerk</div>
              </article>
              <article className="account-card account-card--stat"><Sparkles /><strong>{orders.data?.data.filter((order) => order.status === 'paid').length ?? 0}</strong><span>LICENSES OWNED</span></article>
              <article className="account-card account-card--stat"><PackageOpen /><strong>{orders.data?.data.length ?? 0}</strong><span>TOTAL ORDERS</span></article>
            </section>

            <section className="orders-section">
              <div className="subsection-heading"><div><span className="eyebrow">HISTORY</span><h2>My purchases</h2></div><span>{orders.data?.data.length ?? 0} records</span></div>
              {orders.isLoading ? (
                <div className="order-list"><div className="order-row order-row--skeleton" /><div className="order-row order-row--skeleton" /></div>
              ) : orders.data?.data.length ? (
                <div className="order-list">
                  {orders.data.data.map((order) => (
                    <article className="order-row" data-has-key={order.accessKey ? 'true' : 'false'} key={order.id}>
                      <div className="order-product"><span><ProductImage name={order.product.name} /></span><div><strong>{order.product.name}</strong><small>{planLabels[order.plan]} · {formatDate(order.createdAt)}</small></div></div>
                      <div className="order-meta"><span>Amount</span><strong>{formatMoney(order.amount, order.currency)}</strong></div>
                      <div className="order-meta"><span>Order</span><strong>#{order.id.slice(-8).toUpperCase()}</strong></div>
                      <span className="status-badge" data-status={order.status}><i />{orderStatusLabels[order.status]}</span>
                      {order.accessKey ? (
                        <AccessKey value={order.accessKey} />
                      ) : order.status === 'paid' ? (
                        <div className="access-key access-key--pending"><span className="access-key__label"><KeyRound size={12} /> License key</span><small>Delivery in progress — it will also arrive by email.</small></div>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-orders"><PackageOpen /><h3>No purchases yet.</h3><p>When you pick a product, your access will show up here.</p><Link className="button button--primary" to="/#products">Browse products <ExternalLink size={16} /></Link></div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  )
}
